import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Armchair, RefreshCw, Settings2, UserPlus, UserX, Users, UtensilsCrossed } from 'lucide-react';
import clsx from 'clsx';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { StatTile } from '@/components/StatTile';
import { EmptyState } from '@/components/EmptyState';
import { useLabels } from '@/contexts/WorkspaceContext';
import { useAuth } from '@/hooks/useAuth';
import { ymd } from '@/services/appointments.service';
import {
  tablesService, quarterHourSlots, nearestQuarterHour,
  type DiningTable, type FloorTableCard, type DayBookingRow, type DayBookingFilter, type TablesSummary,
} from '@/services/tables.service';
import { TableCard } from './TableCard';
import { BookingsPanel } from './BookingsPanel';
import { AssignTableModal } from './AssignTableModal';
import { ManageTablesModal } from './ManageTablesModal';
import { WalkInModal } from './WalkInModal';

const TIMES = quarterHourSlots();

export default function TablesPage() {
  const t = useLabels();
  const { user } = useAuth();
  const isAdmin = user?.role === 'company_admin';

  const [date, setDate] = useState(() => ymd(new Date()));
  const [time, setTime] = useState(() => nearestQuarterHour());
  const [area, setArea] = useState<string | null>(null);

  const [tables, setTables] = useState<DiningTable[]>([]);
  const [floor, setFloor] = useState<FloorTableCard[]>([]);
  const [floorLoading, setFloorLoading] = useState(true);
  const [bookings, setBookings] = useState<DayBookingRow[]>([]);
  const [bookingsLoading, setBookingsLoading] = useState(true);
  // Unfiltered day list, kept alongside the panel's filtered one — a table card can
  // reference a booking (e.g. "waiting") that the panel's active filter (e.g. "seated") hides.
  const [allBookings, setAllBookings] = useState<DayBookingRow[]>([]);
  const [summary, setSummary] = useState<TablesSummary | null>(null);
  const [bookingFilter, setBookingFilter] = useState<'all' | DayBookingFilter>('all');

  const [manageOpen, setManageOpen] = useState(false);
  const [walkInOpen, setWalkInOpen] = useState(false);
  const [assignTarget, setAssignTarget] = useState<DayBookingRow | null>(null);

  const loadTables = useCallback(async () => {
    try { setTables((await tablesService.list(false)).items); } catch { /* interceptor toasts */ }
  }, []);

  const loadFloor = useCallback(async () => {
    setFloorLoading(true);
    try { setFloor((await tablesService.floor(date, time)).tables); }
    catch { /* interceptor toasts */ } finally { setFloorLoading(false); }
  }, [date, time]);

  const loadBookings = useCallback(async () => {
    setBookingsLoading(true);
    try {
      const [filtered, all] = await Promise.all([
        tablesService.bookings(date, bookingFilter === 'all' ? undefined : bookingFilter),
        bookingFilter === 'all' ? null : tablesService.bookings(date),
      ]);
      setBookings(filtered.items);
      setAllBookings(all ? all.items : filtered.items);
    } catch { /* interceptor toasts */ } finally { setBookingsLoading(false); }
  }, [date, bookingFilter]);

  const loadSummary = useCallback(async () => {
    try { setSummary(await tablesService.summary(date)); } catch { /* interceptor toasts */ }
  }, [date]);

  useEffect(() => { void loadTables(); }, [loadTables]);
  useEffect(() => { void loadFloor(); }, [loadFloor]);
  useEffect(() => { void loadBookings(); }, [loadBookings]);
  useEffect(() => { void loadSummary(); }, [loadSummary]);

  const refreshAll = () => { void loadFloor(); void loadBookings(); void loadSummary(); };

  const areas = useMemo(() => {
    const set = new Set<string>();
    tables.forEach((tb) => { if (tb.area) set.add(tb.area); });
    return Array.from(set).sort();
  }, [tables]);

  const visibleFloor = useMemo(() => (area ? floor.filter((c) => c.area === area) : floor), [floor, area]);
  const grouped = useMemo(() => {
    const out: Array<{ area: string; cards: FloorTableCard[] }> = [];
    for (const c of visibleFloor) {
      const key = c.area ?? 'No area';
      let g = out.find((x) => x.area === key);
      if (!g) { g = { area: key, cards: [] }; out.push(g); }
      g.cards.push(c);
    }
    return out;
  }, [visibleFloor]);

  const rowByBookingUid = (bookingUid: string): DayBookingRow | undefined => allBookings.find((b) => b.bookingUid === bookingUid);

  const openAssignFor = (row: DayBookingRow) => setAssignTarget(row);
  const openAssignForCard = (card: FloorTableCard) => {
    if (!card.booking) return;
    const row = rowByBookingUid(card.booking.bookingUid);
    if (row) setAssignTarget(row);
  };

  const seat = async (row: DayBookingRow) => {
    try { await tablesService.setFloorStatus(row.bookingUid, 'seated'); toast.success('Guest seated'); refreshAll(); }
    catch (e: any) { if (!e?.response) toast.error('Could not seat this booking.'); }
  };
  const finish = async (row: DayBookingRow) => {
    try { await tablesService.setFloorStatus(row.bookingUid, 'finished'); toast.success('Table freed up'); refreshAll(); }
    catch (e: any) { if (!e?.response) toast.error('Could not finish this booking.'); }
  };
  const noShow = async (row: DayBookingRow) => {
    try { await tablesService.setFloorStatus(row.bookingUid, 'no_show'); toast.success('Marked as no-show'); refreshAll(); }
    catch (e: any) { if (!e?.response) toast.error('Could not update this booking.'); }
  };

  return (
    <div>
      <Topbar
        title={t('reservations')}
        breadcrumb={`${t('operations')} / ${t('reservations')}`}
        description="The floor, right now — and every booking behind it."
        right={
          <>
            <Button variant="secondary" size="sm" iconLeft={<Settings2 size={14} />} onClick={() => setManageOpen(true)}>Manage tables</Button>
            <Button size="sm" iconLeft={<UserPlus size={14} />} onClick={() => setWalkInOpen(true)}>Seat a walk-in</Button>
          </>
        }
      />

      <div className="w-full px-4 py-6 sm:px-7 sm:py-7 space-y-5">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <StatTile label="Free now" value={summary ? `${summary.tablesFreeNow}/${summary.tablesTotal}` : '—'} icon={<Armchair size={16} />} accent="green" />
          <StatTile label="Covers today" value={summary?.coversToday ?? '—'} icon={<Users size={16} />} accent="gold" />
          <StatTile label="Seated now" value={summary?.seatedNow ?? '—'} icon={<UtensilsCrossed size={16} />} accent="blue" />
          <StatTile label="No-shows today" value={summary?.noShowsToday ?? '—'} icon={<UserX size={16} />} accent="red" />
        </div>

        <div className="flex flex-wrap items-end gap-3 rounded-2xl border border-border bg-surface p-4 shadow-soft">
          <div className="flex flex-col gap-1">
            <span className="text-2xs font-medium text-text-secondary">Date</span>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-9 w-[150px]" />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-2xs font-medium text-text-secondary">Time</span>
            <Select value={time} onChange={(e) => setTime(e.target.value)} className="h-9 w-[110px]">
              {TIMES.map((tm) => <option key={tm} value={tm}>{tm}</option>)}
            </Select>
          </div>
          <Button variant="ghost" size="sm" iconLeft={<RefreshCw size={13} />} onClick={refreshAll}>Refresh</Button>
          <div className="ml-auto flex flex-wrap gap-1.5">
            <button
              type="button"
              onClick={() => setArea(null)}
              className={clsx('rounded-full px-3 py-1 text-2xs font-semibold transition-colors', area === null ? 'bg-primary text-on-primary' : 'bg-surface-alt text-text-secondary hover:bg-primary-soft hover:text-primary')}
            >
              All areas
            </button>
            {areas.map((a) => (
              <button
                key={a}
                type="button"
                onClick={() => setArea(a)}
                className={clsx('rounded-full px-3 py-1 text-2xs font-semibold transition-colors', area === a ? 'bg-primary text-on-primary' : 'bg-surface-alt text-text-secondary hover:bg-primary-soft hover:text-primary')}
              >
                {a}
              </button>
            ))}
          </div>
        </div>

        <div className="grid gap-5 lg:grid-cols-[1fr_360px]">
          <div className="min-w-0 space-y-5">
            {floorLoading ? (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {Array.from({ length: 8 }).map((_, i) => <div key={i} className="skeleton h-24 rounded-xl" />)}
              </div>
            ) : visibleFloor.length === 0 ? (
              <EmptyState
                icon={<Armchair size={26} />}
                title="No tables on the floor yet"
                description="Add your first table to start seating guests."
                action={isAdmin ? <Button iconLeft={<Settings2 size={14} />} onClick={() => setManageOpen(true)}>Manage tables</Button> : undefined}
              />
            ) : (
              grouped.map((g) => (
                <div key={g.area}>
                  <div className="mb-2 text-2xs font-semibold uppercase tracking-[0.12em] text-text-muted">{g.area}</div>
                  <div className="flex flex-wrap gap-3">
                    {g.cards.map((c) => <TableCard key={c.tableUid} card={c} onClick={() => openAssignForCard(c)} />)}
                  </div>
                </div>
              ))
            )}
          </div>

          <BookingsPanel
            rows={bookings}
            loading={bookingsLoading}
            filter={bookingFilter}
            onFilterChange={setBookingFilter}
            onAssign={openAssignFor}
            onSeat={seat}
            onFinish={finish}
            onNoShow={noShow}
          />
        </div>
      </div>

      <AssignTableModal
        open={!!assignTarget}
        onClose={() => setAssignTarget(null)}
        booking={assignTarget}
        tables={tables}
        onAssigned={refreshAll}
      />
      <ManageTablesModal open={manageOpen} onClose={() => setManageOpen(false)} isAdmin={isAdmin} onChanged={() => { void loadTables(); refreshAll(); }} />
      <WalkInModal
        open={walkInOpen}
        onClose={() => setWalkInOpen(false)}
        tables={tables}
        hasReservationService={summary?.hasReservationService ?? true}
        onSeated={refreshAll}
      />
    </div>
  );
}
